import { List, Skeleton, Space, Tag, Typography } from 'antd'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import { fetchVenues, fetchZones, type VenueZone } from '@/features/venue/venueApi'
import type { NasEntry } from './nasApi'

interface NasZoneLinksProps {
  nas: NasEntry
  /** Kartochka ochiqmi — yopiq bo'lsa hech narsa so'ralmaydi */
  enabled: boolean
}

interface ZoneLink {
  zone: VenueZone
  venueId: string
  venueName: string
  /** Nima bo'yicha topildi — taxminni haqiqat qilib ko'rsatmaslik uchun */
  matchedBy: 'identifier' | 'ip'
}

/**
 * Bu NAS qaysi ob'ekt zonasiga bog'langan.
 *
 * <h2>⚠️ BU BOG'LANISH TAXMIN, KONTRAKT EMAS</h2>
 * Backendda "NAS → zona" degan endpoint YO'Q va u bu ish doirasida
 * QO'SHILMADI. Bu yerdagi moslik mavjud ma'lumotdan MIJOZ TOMONDA
 * yig'iladi: `venue.zone` da `nas_identifier` va `nas_ip` ustunlari bor
 * (`venueApi.ts`), ular shu qurilmaning `shortname`/`nasname` si bilan
 * solishtiriladi.
 *
 * Shuning uchun natija "bog'langan" deb emas, "shu belgilar mos keldi"
 * deb ko'rsatiladi va QAYSI maydon mos kelgani aytiladi. Sabab aniq:
 * portal zonani FAQAT `nas_identifier` bo'yicha topadi
 * (`JdbcVenueZoneRepository.findByNas` — `WHERE nas_identifier = :nas`),
 * `nas_ip` esa hech qanday izlashda ishlatilmaydi. Ya'ni IP bo'yicha
 * moslik "portal buni topadi" degani EMAS.
 *
 * ⚠️ NARXI: ob'ektlar ro'yxati + har ob'ekt uchun bitta zona so'rovi
 * (N+1). Shuning uchun faqat KARTOCHKA OCHIQ bo'lganda va faqat
 * `venue.read` huquqi bo'lganda so'raladi — huquqsiz operator uchun
 * bo'lim umuman chizilmaydi (u 403 lar to'plamini ko'rardi).
 */
export function NasZoneLinks({ nas, enabled }: NasZoneLinksProps) {
  const { t } = useTranslation()
  const { canAny } = usePermissions()
  const allowed = enabled && canAny('venue.read')

  const venues = useQuery({
    queryKey: queryKeys.venues.all,
    queryFn: () => fetchVenues(null),
    enabled: allowed,
  })

  const zoneQueries = useQueries({
    queries: (venues.data ?? []).map((venue) => ({
      queryKey: queryKeys.venues.zones(venue.id),
      queryFn: () => fetchZones(venue.id),
      enabled: allowed,
    })),
  })

  if (!allowed) return null

  const loading = venues.isPending || zoneQueries.some((q) => q.isPending)

  const links: ZoneLink[] = (venues.data ?? []).flatMap((venue, index) => {
    const zones = zoneQueries[index]?.data ?? []
    return zones
      .map((zone): ZoneLink | null => {
        if (zone.nasIdentifier && zone.nasIdentifier === nas.shortname) {
          return { zone, venueId: venue.id, venueName: venue.name, matchedBy: 'identifier' }
        }
        if (zone.nasIp && zone.nasIp === nas.nasname) {
          return { zone, venueId: venue.id, venueName: venue.name, matchedBy: 'ip' }
        }
        return null
      })
      .filter((link): link is ZoneLink => link !== null)
  })

  return (
    <>
      <Typography.Title level={5}>{t('nas.zones.title')}</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        {t('nas.zones.hint')}
      </Typography.Paragraph>

      {loading ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : links.length === 0 ? (
        <Typography.Text type="secondary">{t('nas.zones.none')}</Typography.Text>
      ) : (
        <List
          size="small"
          dataSource={links}
          rowKey={(link) => link.zone.id}
          renderItem={(link) => (
            <List.Item>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Space size={6} wrap>
                  <Link to={`/venues/${link.venueId}`}>{link.venueName}</Link>
                  <Typography.Text>·</Typography.Text>
                  <Typography.Text strong>{link.zone.name}</Typography.Text>
                  <Typography.Text code>{link.zone.code}</Typography.Text>
                </Space>
                <Tag color={link.matchedBy === 'identifier' ? 'blue' : 'default'}>
                  {t(`nas.zones.matchedBy.${link.matchedBy}`)}
                </Tag>
              </Space>
            </List.Item>
          )}
        />
      )}
    </>
  )
}
